/**
 * Element anchoring + DOM snapshot capture for the site-review feedback bar.
 *
 * The snapshot is a self-contained HTML document of the page EXACTLY as the
 * reviewer saw it at click time (frozen carousel frame, open menus, typed
 * input values). The API renders it in real Chrome via Browserless for a
 * pixel-faithful screenshot — no html2canvas-style CSS misrendering.
 */

const SNAPSHOT_MAX_CHARS = 3800000;

/** Shortest CSS selector that uniquely matches el, biased toward stable anchors. */
export function cssPath(el) {
    if (!(el instanceof Element)) return null;

    if (el.id && isStableToken(el.id) && document.querySelectorAll('#' + CSS.escape(el.id)).length === 1) {
        return '#' + CSS.escape(el.id);
    }

    const segments = [];
    let node = el;
    let depth = 0;

    while (node && node.nodeType === 1 && node !== document.documentElement && depth < 14) {
        let segment = node.tagName.toLowerCase();

        if (node.id && isStableToken(node.id)) {
            segments.unshift('#' + CSS.escape(node.id));
            const candidate = segments.join(' > ');
            if (matchesUnique(candidate, el)) return candidate;
            node = node.parentElement;
            depth++;
            continue;
        }

        const stableClasses = Array.from(node.classList).filter(isStableToken).slice(0, 3);
        if (stableClasses.length) {
            segment += '.' + stableClasses.map((c) => CSS.escape(c)).join('.');
        }

        // Disambiguate against same-tag siblings when needed.
        const parent = node.parentElement;
        if (parent) {
            const sameTag = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
            if (sameTag.length > 1) {
                segment += `:nth-of-type(${sameTag.indexOf(node) + 1})`;
            }
        }

        segments.unshift(segment);
        const candidate = segments.join(' > ');
        if (matchesUnique(candidate, el)) return candidate;

        node = node.parentElement;
        depth++;
    }

    const full = 'html > ' + segments.join(' > ');
    return matchesUnique(full, el) ? full : segments.join(' > ');
}

function matchesUnique(selector, el) {
    try {
        const found = document.querySelectorAll(selector);
        return found.length === 1 && found[0] === el;
    } catch (e) {
        return false;
    }
}

/**
 * Transient STATE classes — they move to a different element as the page runs
 * (carousel advance, menu toggle), so a selector built on one is only "unique"
 * for the frame it was captured in. Real case: `swiper-slide-active` baked
 * into a pin's selector pointed at whichever slide was active later.
 */
const VOLATILE_TOKEN = new RegExp(
    '^(active|inactive|current|selected|open|opened|closed|show|shown|hide|hidden|visible|collapsed|expanded|'
    + 'focus|focused|hover|animating|animated|in-view|w--current|elementor-active|'
    + 'is-(active|open|selected|visible|current|shown|hidden)|'
    + 'slick-(active|current|center|cloned)|'
    + 'swiper-slide-(active|next|prev|visible|fully-visible|duplicate(-\\w+)*)|'
    + 'owl-(active|current))$',
    'i'
);

/**
 * Heuristic: skip machine-generated tokens (CSS-modules hashes, Elementor
 * element ids, utility soup) that won't survive a rebuild of the page, and
 * transient state classes that won't survive the next carousel tick.
 */
function isStableToken(token) {
    if (!token || token.length > 40) return false;
    if (VOLATILE_TOKEN.test(token)) return false;
    if (/\d{4,}/.test(token)) return false;          // long digit runs (elementor-element-3f9a12)
    if (/^[a-z0-9]{8,}$/i.test(token) && /\d/.test(token) && !/[-_]/.test(token)) return false; // hash-like
    if (/^(css|jsx|sc)-/.test(token)) return false;  // styled-components / css-modules
    return true;
}

/**
 * Human-readable description of what was pinned — for task descriptions,
 * where a CSS selector is noise. E.g. `Button "Book a Demo"`, `Image (logo.png)`.
 */
export function elementLabel(el) {
    if (!(el instanceof Element)) return null;

    const text = (el.innerText || el.getAttribute('alt') || el.getAttribute('aria-label')
        || el.getAttribute('placeholder') || el.getAttribute('title') || '')
        .trim().replace(/\s+/g, ' ').slice(0, 60);

    // Classify by the nearest SEMANTIC ancestor — a click on a button's inner
    // span is still "the button".
    const semantic = el.closest('a,button,[role="button"],input,select,textarea,label,h1,h2,h3,h4,h5,img,svg,video,iframe,nav,footer,header,form') || el;
    const tag = semantic.tagName.toLowerCase();
    const kinds = {
        a: 'Link', button: 'Button', input: 'Field', select: 'Dropdown', textarea: 'Field',
        label: 'Label', img: 'Image', svg: 'Icon', video: 'Video', iframe: 'Embed',
        nav: 'Navigation', footer: 'Footer', header: 'Header', form: 'Form',
        h1: 'Heading', h2: 'Heading', h3: 'Heading', h4: 'Heading', h5: 'Heading',
    };
    let kind = kinds[tag];
    if (!kind) {
        kind = /button/i.test(semantic.className || '') ? 'Button'
            : (el.tagName === 'P' || el.tagName === 'SPAN' ? 'Text' : 'Section');
    }

    if (kind === 'Image') {
        const src = (semantic.getAttribute('src') || '').split('/').pop().split('?')[0].slice(0, 40);
        return text ? `Image “${text}”` : (src ? `Image (${src})` : 'Image');
    }

    return text ? `${kind} “${text}”` : `${kind} <${el.tagName.toLowerCase()}>`;
}

/** Absolute XPath (index-based) — the fallback anchor when the selector breaks. */
export function xPath(el) {
    if (!(el instanceof Element)) return null;
    const parts = [];
    let node = el;
    while (node && node.nodeType === 1) {
        let index = 1;
        let sibling = node.previousElementSibling;
        while (sibling) {
            if (sibling.tagName === node.tagName) index++;
            sibling = sibling.previousElementSibling;
        }
        parts.unshift(`${node.tagName.toLowerCase()}[${index}]`);
        node = node.parentElement;
    }
    return '/' + parts.join('/');
}

export function resolveXPath(xpath) {
    try {
        return document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
    } catch (e) {
        return null;
    }
}

/**
 * Serialize the current DOM into a standalone HTML string.
 * Returns null when the result is too large to ship (caller omits the
 * snapshot; the API then renders the live URL instead).
 *
 * @param {Element[]} excludeRoots our own UI hosts — never part of the page
 */
export function serializeDom(excludeRoots = []) {
    try {
        // Tag stylesheet owner nodes so we can find them in the clone, then
        // clean the attributes up afterwards.
        const styleOwners = [];
        Array.from(document.styleSheets).forEach((sheet, i) => {
            if (sheet.ownerNode && sheet.ownerNode.setAttribute) {
                sheet.ownerNode.setAttribute('data-kmd-css', String(i));
                styleOwners.push(sheet.ownerNode);
            }
        });

        const clone = document.documentElement.cloneNode(true);

        try {
            syncTrees(document.documentElement, clone, excludeRoots);
            inlineStylesheets(clone);
        } finally {
            styleOwners.forEach((n) => n.removeAttribute('data-kmd-css'));
        }

        const head = clone.querySelector('head') || clone;

        // Resolve any leftover relative URLs (inline style url(...), etc.).
        const base = document.createElement('base');
        base.setAttribute('href', document.baseURI);
        head.insertBefore(base, head.firstChild);

        // Freeze animations so the render settles deterministically.
        const freeze = document.createElement('style');
        freeze.textContent = '*,*::before,*::after{animation:none!important;transition:none!important;cursor:auto!important}';
        head.appendChild(freeze);

        const html = '<!DOCTYPE html>\n' + clone.outerHTML;
        return html.length > SNAPSHOT_MAX_CHARS ? null : html;
    } catch (e) {
        return null;
    }
}

/**
 * Walk the original and cloned trees in lockstep (cloneNode preserves
 * structure) copying live state the clone can't carry, absolutizing URLs,
 * and marking nodes to strip. Removals happen after the walk so the two
 * trees stay parallel throughout.
 */
function syncTrees(orig, clone, excludeRoots) {
    const toRemove = [];
    const stack = [[orig, clone]];

    while (stack.length) {
        const [o, c] = stack.pop();
        if (!o || !c || o.nodeType !== 1) continue;

        const tag = o.tagName;

        if (excludeRoots.includes(o) || tag === 'SCRIPT' || tag === 'NOSCRIPT') {
            toRemove.push(c);
            continue;
        }

        if (tag === 'IFRAME' || tag === 'OBJECT' || tag === 'EMBED') {
            const rect = o.getBoundingClientRect();
            const ph = document.createElement('div');
            ph.style.cssText = `width:${rect.width}px;height:${rect.height}px;background:#f3f4f6;border:1px dashed #cbd5e1;box-sizing:border-box;display:inline-block;`;
            if (c.parentNode) c.parentNode.replaceChild(ph, c);
            continue;
        }

        if (tag === 'INPUT') {
            if (o.type === 'checkbox' || o.type === 'radio') {
                if (o.checked) c.setAttribute('checked', '');
                else c.removeAttribute('checked');
            } else if (o.type !== 'password') {
                c.setAttribute('value', o.value);
            } else {
                c.setAttribute('value', o.value ? '••••••' : '');
            }
        } else if (tag === 'TEXTAREA') {
            c.textContent = o.value;
        } else if (tag === 'SELECT') {
            const options = o.querySelectorAll('option');
            const cloneOptions = c.querySelectorAll('option');
            options.forEach((opt, i) => {
                if (cloneOptions[i]) {
                    if (opt.selected) cloneOptions[i].setAttribute('selected', '');
                    else cloneOptions[i].removeAttribute('selected');
                }
            });
        } else if (tag === 'CANVAS') {
            try {
                const img = document.createElement('img');
                img.src = o.toDataURL();
                img.width = o.width;
                img.height = o.height;
                if (o.getAttribute('style')) img.setAttribute('style', o.getAttribute('style'));
                if (o.getAttribute('class')) img.setAttribute('class', o.getAttribute('class'));
                if (c.parentNode) c.parentNode.replaceChild(img, c);
                continue;
            } catch (e) { /* tainted canvas — keep the blank element */ }
        }

        // Absolutize resource URLs via the live element's resolved properties.
        if (tag === 'IMG') {
            if (o.currentSrc || o.src) c.setAttribute('src', o.currentSrc || o.src);
            c.removeAttribute('srcset');
            c.removeAttribute('loading'); // lazy images must load in the renderer
        } else if (tag === 'SOURCE') {
            if (o.src) c.setAttribute('src', o.src);
            c.removeAttribute('srcset');
        } else if ((tag === 'VIDEO' || tag === 'AUDIO') && o.src) {
            c.setAttribute('src', o.src);
            c.removeAttribute('autoplay');
        } else if (tag === 'LINK' && o.href) {
            c.setAttribute('href', o.href);
        }

        const oc = o.children;
        const cc = c.children;
        // Children may diverge if a replacement already happened — pair by index defensively.
        for (let i = 0; i < oc.length && i < cc.length; i++) {
            stack.push([oc[i], cc[i]]);
        }
    }

    toRemove.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
}

/**
 * Replace readable (same-origin) stylesheet links in the clone with their
 * current rules — this captures CSSOM state and avoids re-fetch drift.
 * Cross-origin sheets stay as absolutized <link>s for the renderer to fetch.
 */
function inlineStylesheets(clone) {
    Array.from(document.styleSheets).forEach((sheet, i) => {
        const cloneNode = clone.querySelector(`[data-kmd-css="${i}"]`);
        if (!cloneNode) return;
        cloneNode.removeAttribute('data-kmd-css');

        let cssText = null;
        try {
            cssText = Array.from(sheet.cssRules).map((r) => r.cssText).join('\n');
        } catch (e) {
            return; // cross-origin — the absolutized <link> stays
        }

        if (cloneNode.tagName === 'LINK') {
            const style = document.createElement('style');
            style.textContent = resolveCssUrls(cssText, sheet.href || document.baseURI);
            cloneNode.parentNode.replaceChild(style, cloneNode);
        } else {
            // <style> node: refresh with CSSOM state (rules may have been mutated at runtime).
            cloneNode.textContent = resolveCssUrls(cssText, document.baseURI);
        }
    });
}

/** url(foo.png) inside an inlined sheet resolves against the SHEET's URL, not the page. */
function resolveCssUrls(cssText, baseHref) {
    return cssText.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (match, quote, url) => {
        if (/^(data:|blob:|https?:|\/\/|#)/i.test(url)) return match;
        try {
            return `url("${new URL(url, baseHref).href}")`;
        } catch (e) {
            return match;
        }
    });
}
